/* eslint-disable class-methods-use-this */
import { Cookies, Requests, arrayBufferToImageSrcBase64 } from '@nx-smartmonkey/shared/helpers';
import { CookiesConstants } from '../../constants/Cookies';
import countries from '../../constants/Countries';
import { PlannerCredentials } from '../../domain/credentials/PlannerCredentials';
import { PlannerCredentialsDTO } from '../../domain/credentials/PlannerCredentialsDTO';
import { User } from '../../domain/user/User';
import { UserDTO } from '../../domain/user/UserDTO';
import { PlannerCredentialsMapper } from '../../mappers/credentials/PlannerCredentialsMapper';
import { UserMapper } from '../../mappers/user/UserMapper';
import { SessionRepository } from '../session/SessionRepository';
import { UserLocationCountryProps, UserRepository } from './UserRepository';

export class UserRepositoryApi implements UserRepository {
  private userLocationCountry: UserLocationCountryProps | undefined;

  constructor(private requests: Requests, private sessionRepository: SessionRepository, private cookies: Cookies) {}

  setUserLocationByCountry(countryCode?: string) {
    const countryObject = countries[`${countryCode}`];
    if (countryObject?.coordinates?.latitude && countryObject?.coordinates?.longitude) {
      this.userLocationCountry = {
        latitude: countryObject.coordinates.latitude,
        longitude: countryObject.coordinates.longitude,
        country: countryCode!,
        mapZoomLevel: countryObject.mapZoomLevel,
      };
    }
  }

  getUserLocationByCountry(): UserLocationCountryProps | undefined {
    return this.userLocationCountry;
  }

  async validate({ token }: { token: string }): Promise<string> {
    const response = await this.requests.get({
      uri: `/user/validate`,
      params: {
        token,
      },
    });

    return response.data.token;
  }

  async validatePhone({ otp_code }: { otp_code: string }): Promise<void> {
    await this.requests.get({
      uri: `/user/validate-phone`,
      params: {
        otp_code,
        otp_token: this.cookies.getCookie(CookiesConstants.R_PLANNER_COOKIE_2FA),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async newPassword({ token, password }: { token: string; password: string }): Promise<void> {
    await this.requests.post({
      uri: `/user/new-password`,
      data: {
        token,
        password,
      },
    });
  }

  async fetchResetPassword({ email }: { email: string }): Promise<void> {
    await this.requests.get({
      uri: `/user/reset-password`,
      params: {
        email,
      },
    });
  }

  async resendValidationEmail({ email }: { email: string }): Promise<void> {
    await this.requests.get({
      uri: `/user/resend-validation-email`,
      params: {
        email,
      },
    });
  }

  async resendValidationPhone(): Promise<string> {
    const response = await this.requests.get({
      uri: `/user/resend-validation-phone`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const { token } = response.data;

    this.cookies.setCookie(CookiesConstants.R_PLANNER_COOKIE_2FA, token, 360);

    return token;
  }

  async validateNewPasswordToken({ token }: { token: string }): Promise<void> {
    await this.requests.get({
      uri: `/user/new-password/validate`,
      params: {
        token,
      },
    });
  }

  async fetch(): Promise<User> {
    const response = await this.requests.get({
      uri: `/user/details`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const user = response.data as UserDTO;

    return UserMapper.toDomain(user);
  }

  async fetchAdmin(): Promise<User> {
    const response = await this.requests.get({
      uri: `/user/details`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const user = response.data as UserDTO;

    return UserMapper.toDomain(user);
  }

  async retrieveCredentials(): Promise<PlannerCredentials> {
    const response = await this.requests.get({
      uri: `/user/credentials`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const credentials = response.data as PlannerCredentialsDTO;
    return PlannerCredentialsMapper.toDomain(credentials);
  }

  async save(user: User): Promise<User> {
    const response = await this.requests.put({
      uri: `/user`,
      data: {
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
        language: user.language.value,
        notifications: {
          analytics_email: user.notifications.analytics_email,
        },
        two_factor: {
          email_enabled: user.two_factor.email_enabled,
          sms_enabled: user.two_factor.sms_enabled,
        },
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedUser = response.data as UserDTO;

    return UserMapper.toDomain(updatedUser);
  }

  async fetchAvatar({ user }: { user: User }): Promise<User> {
    if (!user.avatar_url) {
      return user;
    }

    const requests = Requests.create(``);

    const response = await requests.get({
      uri: user.avatar_url,
      responseType: `arraybuffer`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    user.user_avatar = await arrayBufferToImageSrcBase64({
      data: response.data,
      dataType: response.headers[`content-type`].toLowerCase(),
    });

    return user;
  }

  async createAvatar({
    user,
    fileName,
    mimeType,
    file,
  }: {
    user: User;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<User> {
    const formData = new FormData();
    formData.append(`name`, fileName);
    formData.append(`mime`, mimeType);
    formData.append(`data`, file);

    const response = await this.requests.post({
      uri: `/user/avatar`,
      data: formData,
      headers: {
        'content-type': `multipart/form-data`,
        ...this.sessionRepository.getAuthenticationHeader(),
      },
    });

    user.avatar_url = response.data.avatar_url;

    return user;
  }

  async deleteAvatar({ user }: { user: User }): Promise<User> {
    await this.requests.delete({
      uri: `/user/avatar`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    user.avatar_url = undefined;
    user.user_avatar = undefined;
    return user;
  }
}
